import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Play, Pause, RotateCcw, CheckCircle2, AlertTriangle, Camera, MessageSquarePlus, Clock, ShieldAlert, PackageCheck } from "lucide-react";
import { API } from "@/lib/api";
import { statusMeta, priorityMeta, PAUSE_REASONS, BLOCK_REASONS, reasonLabel, fmtDuration, fmtQty } from "@/pages/tasks/taskConstants";

export default function TaskDetail({ taskId, open, onClose, onChanged, mode = "staff", canManage = false }) {
  const [task, setTask] = useState(null);
  const [types, setTypes] = useState({});
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState(null); // {kind, ...}
  const [comment, setComment] = useState("");

  const load = useCallback(() => {
    if (!taskId) return;
    api.get(`/tasks/${taskId}`).then((r) => setTask(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [taskId]);

  useEffect(() => { if (open) { load(); api.get("/task-types").then((r) => setTypes(Object.fromEntries((r.data || []).map((t) => [t.code, t])))).catch(() => {}); } }, [open, load]);

  const ttype = task ? types[task.task_type_code] || {} : {};
  const refresh = () => { load(); onChanged && onChanged(); };

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try { await api.post(path, body || {}); toast.success(okMsg || "Done"); setDlg(null); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/tasks/${taskId}/comment`, { text: comment }); setComment(""); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm">{task?.task_no}</span>
            <span>{task?.task_type_name}</span>
            {task && <Badge variant="outline" className={statusMeta(task.status).cls} data-testid="task-detail-status">{statusMeta(task.status).label}</Badge>}
            {task && <Badge variant="outline" className={priorityMeta(task.priority).cls}>{priorityMeta(task.priority).label}</Badge>}
          </DialogTitle>
          <DialogDescription>Operational task details, SKU work lines, timers and discussion.</DialogDescription>
        </DialogHeader>

        {!task ? <div className="py-8 text-center text-slate-400">Loading…</div> : (
          <div className="space-y-4">
            {/* Meta */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {task.warehouse_code && <Meta label="WH Source" value={task.warehouse_code} />}
              <Meta label="Assigned" value={task.assigned_employee || task.assigned_team || "Pool"} />
              <Meta label="Active Time" value={fmtDuration(task.active_seconds)} icon={<Clock className="w-3 h-3" />} />
              <Meta label="Paused" value={fmtDuration(task.paused_seconds)} />
              <Meta label="Blocked Wait" value={fmtDuration(task.blocked_seconds)} />
              {task.destination_rack && <Meta label="Dest. Rack" value={task.destination_rack} />}
              {task.handover_location && <Meta label="Pickup Area" value={task.handover_location} />}
              {task.roll_qty != null && <Meta label="Rolls" value={task.roll_qty} />}
              {task.due_date && <Meta label="Due" value={task.due_date} />}
            </div>
            {task.notes && <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">{task.notes}</div>}

            {/* Block banner */}
            {task.status === "blocked" && task.block && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm" data-testid="task-block-banner">
                <div className="flex items-center gap-1.5 font-medium text-rose-800"><ShieldAlert className="w-4 h-4" />Blocked — {reasonLabel(BLOCK_REASONS, task.block.reason)}</div>
                {task.block.remark && <div className="text-xs text-rose-700 mt-1">{task.block.remark}</div>}
                {task.block.photo && <a className="text-xs text-rose-700 underline" href={`${API}/tasks/${task.id}/block-photo`} target="_blank" rel="noreferrer">View photo</a>}
                {mode === "admin" && canManage && (
                  <Button size="sm" className="mt-2 bg-indigo-600 hover:bg-indigo-700" data-testid="task-resolve-block-btn"
                    onClick={() => setDlg({ kind: "resolve" })}>Resolve &amp; mark Ready to Resume</Button>
                )}
              </div>
            )}

            {/* SKU lines */}
            {task.multi_sku && (
              <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="task-lines">
                <div className="px-3 py-2 bg-slate-50 text-xs font-semibold border-b">SKU Work Lines</div>
                {(task.lines || []).map((ln) => (
                  <LineRow key={ln.id} ln={ln} ttype={ttype} mode={mode} canManage={canManage} busy={busy}
                    onAction={(path, body, msg) => act(path, body, msg)}
                    onDamage={() => setDlg({ kind: "damage", line: ln })}
                    onCompleteLine={() => setDlg({ kind: "complete_line", line: ln })}
                    onBlockLine={() => setDlg({ kind: "block_line", line: ln })} />
                ))}
              </div>
            )}

            {/* Work actions (staff) */}
            {mode === "staff" && (
              <div className="flex flex-wrap gap-2" data-testid="task-actions">
                {["taken", "to_do"].includes(task.status) && <Button data-testid="task-start-btn" disabled={busy} onClick={() => act(`/tasks/${task.id}/start`, {}, "Started")} className="bg-emerald-600 hover:bg-emerald-700"><Play className="w-4 h-4 mr-1" />Start</Button>}
                {task.status === "in_progress" && <>
                  <Button data-testid="task-pause-btn" variant="outline" disabled={busy} onClick={() => setDlg({ kind: "pause" })}><Pause className="w-4 h-4 mr-1" />Pause</Button>
                  <Button data-testid="task-block-btn" variant="outline" disabled={busy} onClick={() => setDlg({ kind: "block" })} className="border-rose-300 text-rose-700"><AlertTriangle className="w-4 h-4 mr-1" />Needs Attention</Button>
                  {!task.multi_sku && <Button data-testid="task-complete-btn" disabled={busy} onClick={() => setDlg({ kind: "complete" })} className="bg-slate-900 hover:bg-slate-800"><CheckCircle2 className="w-4 h-4 mr-1" />Complete</Button>}
                </>}
                {["paused", "ready_to_resume", "partially_completed"].includes(task.status) && <Button data-testid="task-resume-btn" disabled={busy} onClick={() => act(`/tasks/${task.id}/resume`, {}, "Resumed")} className="bg-emerald-600 hover:bg-emerald-700"><RotateCcw className="w-4 h-4 mr-1" />Resume</Button>}
                {task.multi_sku && ["in_progress", "partially_completed"].includes(task.status) && <Button data-testid="task-complete-parent-btn" disabled={busy} onClick={() => setDlg({ kind: "complete" })} className="bg-slate-900 hover:bg-slate-800"><PackageCheck className="w-4 h-4 mr-1" />Complete Task</Button>}
                {task.status === "blocked" && <span className="text-sm text-rose-600 py-2">Blocked — awaiting Admin resolution.</span>}
              </div>
            )}

            {/* Admin actions */}
            {mode === "admin" && canManage && !["done", "cancelled"].includes(task.status) && (
              <div className="flex flex-wrap gap-2" data-testid="task-admin-actions">
                <Button size="sm" variant="outline" data-testid="task-reassign-btn" onClick={() => setDlg({ kind: "reassign" })}>Reassign</Button>
                <Button size="sm" variant="outline" data-testid="task-release-btn" onClick={() => setDlg({ kind: "release" })}>Force Release</Button>
                <Button size="sm" variant="outline" data-testid="task-priority-btn" onClick={() => setDlg({ kind: "priority" })}>Set Priority</Button>
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" data-testid="task-cancel-btn" onClick={() => setDlg({ kind: "cancel" })}>Cancel Task</Button>
              </div>
            )}

            {/* Comments */}
            <div className="rounded-lg border border-slate-200" data-testid="task-comments">
              <div className="px-3 py-2 bg-slate-50 text-xs font-semibold border-b">Comments</div>
              <div className="max-h-40 overflow-y-auto divide-y">
                {(task.comments || []).length === 0 ? <div className="px-3 py-4 text-center text-xs text-slate-400">No comments</div>
                  : task.comments.map((c) => (
                    <div key={c.id} className="px-3 py-2 text-sm">
                      <div className="flex justify-between"><span className="font-medium text-slate-700">{c.employee_id || c.user}</span><span className="text-[10px] text-slate-400 font-mono">{(c.at || "").slice(0, 16).replace("T", " ")}</span></div>
                      <div className="text-slate-600">{c.text}</div>
                    </div>
                  ))}
              </div>
              <div className="flex gap-2 p-2 border-t">
                <Input data-testid="task-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…" className="h-9" />
                <Button size="sm" data-testid="task-comment-send" onClick={addComment}><MessageSquarePlus className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* Audit (admin) */}
            {mode === "admin" && task.audit && (
              <details className="text-xs" data-testid="task-audit">
                <summary className="cursor-pointer text-slate-500">Audit history ({task.audit.length})</summary>
                <div className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                  {task.audit.map((a) => <div key={a.id} className="font-mono text-[11px] text-slate-500">{(a.at || "").slice(0, 16).replace("T", " ")} · {a.action} · {a.by}{a.reason ? ` · ${a.reason}` : ""}</div>)}
                </div>
              </details>
            )}
          </div>
        )}

        <ActionDialog dlg={dlg} setDlg={setDlg} task={task} ttype={ttype} busy={busy} act={act} taskId={taskId} refresh={refresh} />
      </DialogContent>
    </Dialog>
  );
}

const Meta = ({ label, value, icon }) => (
  <div className="bg-slate-50 rounded px-2 py-1.5"><div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">{icon}{label}</div><div className="font-medium text-slate-700 truncate">{value}</div></div>
);

function LineRow({ ln, ttype, mode, canManage, busy, onAction, onDamage, onCompleteLine, onBlockLine }) {
  const [rack, setRack] = useState(ln.destination_rack || "");
  return (
    <div className="px-3 py-2 border-b last:border-0 text-sm" data-testid={`task-line-${ln.sku}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <span className="font-mono font-semibold">{ln.sku}</span>
          <span className="text-slate-500 ml-1 text-xs">{ln.name}</span>
          {ln.packing_requirement && <span className="ml-1 text-[10px] text-violet-600">· {ln.packing_requirement}</span>}
        </div>
        <Badge variant="outline" className={statusMeta(ln.status).cls}>{statusMeta(ln.status).label}</Badge>
      </div>
      <div className="text-xs text-slate-500 mt-0.5">Expected {fmtQty(ln.expected_qty)} · Done {fmtQty(ln.completed_qty)} · Damaged {fmtQty(ln.damaged_qty)}</div>
      {ttype.requires_rack && ln.status !== "done" && mode === "staff" && (
        <Input value={rack} onChange={(e) => setRack(e.target.value)} placeholder="Destination rack (required)" className="h-8 mt-1 text-sm" data-testid={`line-rack-${ln.sku}`} />
      )}
      {mode === "staff" && ln.status !== "done" && ln.status !== "cancelled" && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {ln.status === "blocked"
            ? <span className="text-xs text-rose-600">Blocked — awaiting Admin.</span>
            : <>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`line-complete-${ln.sku}`} disabled={busy} onClick={() => onCompleteLine({ ...ln, destination_rack: rack })}>Complete</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700" data-testid={`line-block-${ln.sku}`} disabled={busy} onClick={() => onBlockLine()}>Block</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-700" data-testid={`line-damage-${ln.sku}`} disabled={busy} onClick={() => onDamage()}><Camera className="w-3 h-3 mr-1" />Damage</Button>
              </>}
        </div>
      )}
      {mode === "admin" && canManage && ln.status === "blocked" && (
        <Button size="sm" className="h-7 text-xs mt-1.5 bg-indigo-600 hover:bg-indigo-700" data-testid={`line-resolve-${ln.sku}`}
          onClick={() => onAction(`/tasks/lines/${ln.id}/resolve-block`, { resolution_note: "Resolved" }, "Line ready to resume")}>Resolve Line Block</Button>
      )}
    </div>
  );
}

function ActionDialog({ dlg, setDlg, task, ttype, busy, act, taskId, refresh }) {
  const [f, setF] = useState({});
  const [file, setFile] = useState(null);
  useEffect(() => { setF({}); setFile(null); }, [dlg]);
  if (!dlg) return null;
  const close = () => setDlg(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadThen = async (preFn) => {
    // used by block (optional photo) — block first, then upload photo
    await preFn();
  };

  const submit = async () => {
    const k = dlg.kind;
    if (k === "pause") return act(`/tasks/${taskId}/pause`, { reason: f.reason, note: f.note }, "Paused");
    if (k === "resolve") return act(`/tasks/${taskId}/resolve-block`, { resolution_note: f.note }, "Resolved");
    if (k === "reassign") return act(`/tasks/${taskId}/reassign`, { assigned_employee: f.employee || null, assigned_team: f.team || null, reason: f.reason }, "Reassigned");
    if (k === "release") return act(`/tasks/${taskId}/release`, { reason: f.reason || "released" }, "Released");
    if (k === "cancel") return act(`/tasks/${taskId}/cancel`, { reason: f.reason }, "Cancelled");
    if (k === "priority") return act(`/tasks/${taskId}/priority`, { priority: f.priority, reason: f.reason }, "Priority updated");
    if (k === "complete") return act(`/tasks/${taskId}/complete`, { handover_location: f.handover, destination_rack: f.rack, roll_qty: f.roll ? Number(f.roll) : null, completed_qty: f.qty !== undefined && f.qty !== "" ? Number(f.qty) : null, confirm_overage: !!f.overage, note: f.note }, "Completed");
    if (k === "complete_line") return act(`/tasks/lines/${dlg.line.id}/complete`, { completed_qty: f.qty !== undefined && f.qty !== "" ? Number(f.qty) : null, damaged_qty: f.damaged ? Number(f.damaged) : null, destination_rack: dlg.line.destination_rack || f.rack, confirm_overage: !!f.overage }, "SKU line completed");
    if (k === "block_line") return act(`/tasks/lines/${dlg.line.id}/block`, { reason: f.reason, remark: f.remark }, "SKU line blocked");
    if (k === "block") {
      try {
        await api.post(`/tasks/${taskId}/block`, { reason: f.reason, remark: f.remark });
        if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/tasks/${taskId}/block-photo`, fd); }
        toast.success("Task blocked — Admin notified"); setDlg(null); refresh();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (k === "damage") {
      if (!file) return toast.error("A photo is required");
      try {
        const fd = new FormData(); fd.append("file", file); fd.append("damaged_qty", String(f.damaged || 0)); fd.append("note", f.note || "");
        await api.post(`/tasks/lines/${dlg.line.id}/damage`, fd);
        toast.success("Damage recorded — Admin notified"); setDlg(null); refresh();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
  };

  const titles = { pause: "Pause Task", resolve: "Resolve Block", reassign: "Reassign Task", release: "Force Release", cancel: "Cancel Task", priority: "Set Priority", complete: "Complete Task", complete_line: "Complete SKU Line", block_line: "Block SKU Line", block: "Needs Attention / Block", damage: "Report Damage" };

  return (
    <Dialog open onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md" data-testid={`task-action-${dlg.kind}`}>
        <DialogHeader><DialogTitle>{titles[dlg.kind]}</DialogTitle><DialogDescription>{dlg.line ? `SKU ${dlg.line.sku}` : task?.task_no}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {dlg.kind === "pause" && <>
            <Label className="text-xs">Reason</Label>
            <Select value={f.reason} onValueChange={(v) => set("reason", v)}><SelectTrigger data-testid="pause-reason"><SelectValue placeholder="Select reason" /></SelectTrigger><SelectContent>{PAUSE_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
            {f.reason === "other" && <Textarea data-testid="pause-note" placeholder="Note" value={f.note || ""} onChange={(e) => set("note", e.target.value)} rows={2} />}
          </>}
          {(dlg.kind === "block" || dlg.kind === "block_line") && <>
            <Label className="text-xs">Block reason</Label>
            <Select value={f.reason} onValueChange={(v) => set("reason", v)}><SelectTrigger data-testid="block-reason"><SelectValue placeholder="Select reason" /></SelectTrigger><SelectContent>{BLOCK_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
            <Textarea data-testid="block-remark" placeholder={f.reason === "other" ? "Explanation (required)" : "Remark (optional)"} value={f.remark || ""} onChange={(e) => set("remark", e.target.value)} rows={2} />
            {dlg.kind === "block" && <div><Label className="text-xs">Photo (optional)</Label><input type="file" accept="image/*" capture="environment" data-testid="block-photo" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm mt-1" /></div>}
          </>}
          {dlg.kind === "damage" && <>
            <Label className="text-xs">Damaged quantity</Label>
            <Input type="number" data-testid="damage-qty" value={f.damaged || ""} onChange={(e) => set("damaged", e.target.value)} />
            <Label className="text-xs">Photo (required)</Label>
            <input type="file" accept="image/*" capture="environment" data-testid="damage-photo" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
            <Textarea data-testid="damage-note" placeholder="Note" value={f.note || ""} onChange={(e) => set("note", e.target.value)} rows={2} />
          </>}
          {(dlg.kind === "complete" || dlg.kind === "complete_line") && <>
            {ttype.requires_handover && dlg.kind === "complete" && <><Label className="text-xs">Warehouse Pickup Area (confirm)</Label><Input data-testid="complete-handover" value={f.handover || ""} onChange={(e) => set("handover", e.target.value)} placeholder="e.g. Pickup Area A" /></>}
            {ttype.requires_rack && dlg.kind === "complete" && !task?.multi_sku && <><Label className="text-xs">Destination Rack (required)</Label><Input data-testid="complete-rack" value={f.rack || ""} onChange={(e) => set("rack", e.target.value)} /></>}
            {task?.task_type_code === "BUBBLE_CUT" && dlg.kind === "complete" && <><Label className="text-xs">Rolls completed</Label><Input type="number" data-testid="complete-roll" value={f.roll || ""} onChange={(e) => set("roll", e.target.value)} /></>}
            {ttype.requires_qty && <><Label className="text-xs">Completed quantity</Label><Input type="number" data-testid="complete-qty" value={f.qty ?? ""} onChange={(e) => set("qty", e.target.value)} /><Label className="text-xs">Damaged (optional)</Label><Input type="number" data-testid="complete-damaged" value={f.damaged || ""} onChange={(e) => set("damaged", e.target.value)} /></>}
            <label className="flex items-center gap-2 text-xs"><Checkbox checked={!!f.overage} onCheckedChange={(v) => set("overage", v)} data-testid="complete-overage" />Confirm quantity variance / overage if above expected</label>
          </>}
          {dlg.kind === "resolve" && <Textarea data-testid="resolve-note" placeholder="Resolution note" value={f.note || ""} onChange={(e) => set("note", e.target.value)} rows={2} />}
          {dlg.kind === "reassign" && <>
            <Label className="text-xs">Employee ID (optional)</Label><Input data-testid="reassign-emp" value={f.employee || ""} onChange={(e) => set("employee", e.target.value)} placeholder="FAS003" />
            <Label className="text-xs">or Team</Label><Input data-testid="reassign-team" value={f.team || ""} onChange={(e) => set("team", e.target.value)} placeholder="Packing Team" />
            <Label className="text-xs">Reason</Label><Input data-testid="reassign-reason" value={f.reason || ""} onChange={(e) => set("reason", e.target.value)} />
          </>}
          {dlg.kind === "priority" && <>
            <Label className="text-xs">Priority</Label>
            <Select value={f.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="priority-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{["normal", "high", "urgent", "order_blocking"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          </>}
          {(dlg.kind === "cancel" || dlg.kind === "release") && <><Label className="text-xs">Reason</Label><Textarea data-testid="reason-input" value={f.reason || ""} onChange={(e) => set("reason", e.target.value)} rows={2} /></>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button data-testid="task-action-submit" disabled={busy} onClick={submit}>Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
